import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { ESTADO_PERFIL_VEHICULO, DESCRIPCION_ESTADO_PERFIL, COLOR_ESTADO_PERFIL, ICONO_ESTADO_PERFIL } from '../../../constants/concesionariaConstants';
import { formatearFecha } from '../../../utils/dateUtils';

export default function GestionPerfilesConcesionarias() {
  const [perfiles, setPerfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [filtroConcesionaria, setFiltroConcesionaria] = useState(null);
  const [concesionarias, setConcesionarias] = useState([]);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    preCreados: 0,
    asignados: 0,
    pendientesValidacion: 0,
    activos: 0,
    expirados: 0
  });

  useEffect(() => {
    cargarPerfiles();
    cargarConcesionarias();
  }, [filtroEstado, filtroConcesionaria]);

  const cargarConcesionarias = async () => {
    try {
      const empresasQuery = query(
        collection(db, 'empresas'),
        where('tipoEmpresa', '==', 'concesionaria'),
        orderBy('nombre')
      );
      const empresasSnap = await getDocs(empresasQuery);
      const empresasList = empresasSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setConcesionarias(empresasList);
    } catch (error) {
      console.error('Error cargando concesionarias:', error);
    }
  };

  const cargarPerfiles = async () => {
    setLoading(true);
    try {
      let perfilesQuery = query(
        collection(db, 'perfiles_vehiculos_concesionaria'),
        orderBy('fechaCreacion', 'desc')
      );

      const perfilesSnap = await getDocs(perfilesQuery);
      let perfilesList = perfilesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrar por estado si está seleccionado
      if (filtroEstado) {
        perfilesList = perfilesList.filter(p => p.estado === filtroEstado);
      }

      // Filtrar por concesionaria si está seleccionada
      if (filtroConcesionaria) {
        perfilesList = perfilesList.filter(p => p.concesionariaId === filtroConcesionaria);
      }

      setPerfiles(perfilesList);

      // Calcular estadísticas
      const stats = {
        total: perfilesList.length,
        preCreados: perfilesList.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.PRE_CREADO).length,
        asignados: perfilesList.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.ASIGNADO).length,
        pendientesValidacion: perfilesList.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.PENDIENTE_VALIDACION).length,
        activos: perfilesList.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.ACTIVO).length,
        expirados: perfilesList.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.EXPIRADO).length
      };
      setEstadisticas(stats);

    } catch (error) {
      console.error('Error cargando perfiles:', error);
      alert('Error al cargar perfiles: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const calcularDiasRestantes = (fechaExpiracion) => {
    if (!fechaExpiracion) return null;
    try {
      const fecha = fechaExpiracion.toDate ? fechaExpiracion.toDate() : new Date(fechaExpiracion);
      const ahora = new Date();
      const diffTime = fecha - ahora;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-lg">Cargando perfiles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          📋 Gestión de Perfiles de Concesionarias
        </h1>
        <p className="text-gray-600">
          Visualiza y gestiona todos los perfiles de vehículos generados por concesionarias
        </p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{estadisticas.total}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
        <div className="bg-gray-50 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-700">{estadisticas.preCreados}</div>
          <div className="text-sm text-gray-600">Pre-Creados</div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-yellow-700">{estadisticas.asignados}</div>
          <div className="text-sm text-gray-600">Asignados</div>
        </div>
        <div className="bg-orange-50 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-700">{estadisticas.pendientesValidacion}</div>
          <div className="text-sm text-gray-600">Pendientes Validación</div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-700">{estadisticas.activos}</div>
          <div className="text-sm text-gray-600">Activos</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-700">{estadisticas.expirados}</div>
          <div className="text-sm text-gray-600">Expirados</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Estado
            </label>
            <select
              value={filtroEstado || ''}
              onChange={(e) => setFiltroEstado(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value={ESTADO_PERFIL_VEHICULO.PRE_CREADO}>Pre-Creados</option>
              <option value={ESTADO_PERFIL_VEHICULO.ASIGNADO}>Asignados</option>
              <option value={ESTADO_PERFIL_VEHICULO.PENDIENTE_VALIDACION}>Pendientes de Validación</option>
              <option value={ESTADO_PERFIL_VEHICULO.ACTIVO}>Activos</option>
              <option value={ESTADO_PERFIL_VEHICULO.EXPIRADO}>Expirados</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Concesionaria
            </label>
            <select
              value={filtroConcesionaria || ''}
              onChange={(e) => setFiltroConcesionaria(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas las concesionarias</option>
              {concesionarias.map(concesionaria => (
                <option key={concesionaria.id} value={concesionaria.id}>
                  {concesionaria.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={cargarPerfiles}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              🔄 Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de perfiles */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            Perfiles ({perfiles.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehículo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concesionaria</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Membresía</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Validación</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {perfiles.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No hay perfiles que mostrar
                  </td>
                </tr>
              ) : (
                perfiles.map(perfil => {
                  const diasRestantes = perfil.fechaExpiracionMembresia 
                    ? calcularDiasRestantes(perfil.fechaExpiracionMembresia)
                    : null;
                  
                  return (
                    <tr key={perfil.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {perfil.marca} {perfil.modelo}
                        </div>
                        <div className="text-sm text-gray-500">
                          {perfil.año} - {perfil.tipoVehiculo}
                        </div>
                        {perfil.vin && (
                          <div className="text-xs text-gray-400">VIN: {perfil.vin}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{perfil.concesionariaNombre || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${COLOR_ESTADO_PERFIL[perfil.estado] || 'bg-gray-100 text-gray-800'}`}>
                          {ICONO_ESTADO_PERFIL[perfil.estado] || '📋'} {perfil.estado.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {perfil.clienteNombre ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">{perfil.clienteNombre}</div>
                            <div className="text-sm text-gray-500">{perfil.clienteEmail}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {perfil.membresiaPremiumActiva ? (
                          <div>
                            <span className="text-green-600 font-medium">✓ Activa</span>
                            {diasRestantes !== null && (
                              <div className="text-gray-500 text-xs mt-1">
                                {diasRestantes > 0 ? `${diasRestantes} días restantes` : 'Expirada'}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">No activa</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {perfil.fechaCreacion ? formatearFecha(perfil.fechaCreacion) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {perfil.fechaValidacion ? formatearFecha(perfil.fechaValidacion) : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}




